import React from "react";
import { useNavigate } from "react-router-dom";
import "./LegalDocument.css";

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <main className="legal-page">
      <button type="button" className="legal-back-button" onClick={() => navigate(-1)}>
        Back
      </button>

      <header className="legal-header">
        <p className="legal-kicker">Legal</p>
        <h1>BlueWire Terms of Service</h1>
        <p className="legal-effective"><strong>Effective Date:</strong> 11/04/2026</p>
      </header>

      <section className="legal-section">
        <h2>1. Acceptance of Terms</h2>
        <p>By accessing or using BlueWire, you agree to be bound by these Terms of Service.</p>
      </section>

      <section className="legal-section">
        <h2>2. Eligibility and Verification</h2>
        <p>
          BlueWire is intended exclusively for verified law enforcement personnel
          and authorized public safety professionals who are 21 years of age or older.
        </p>
        <p>
          To access BlueWire, users are required to complete a registration process,
          which may include submission of a valid department-issued email address,
          a department-issued identification credential, and a real-time selfie for
          identity confirmation.
        </p>
        <p>By registering, you represent and warrant that:</p>
        <ul>
          <li>You are affiliated with a legitimate law enforcement or public safety agency.</li>
          <li>All information and documents submitted for verification are accurate and authentic.</li>
          <li>You are the rightful owner of the credentials and identification submitted.</li>
        </ul>
        <p>
          BlueWire reserves the right to approve, deny, or revoke access at its sole
          discretion if verification cannot be confirmed or if fraudulent or misleading
          information is detected.
        </p>
      </section>

      <section className="legal-section">
        <h2>3. User Accounts</h2>
        <p>
          To use BlueWire, you must register an account and provide accurate information
          including your name, work email address, phone number, agency, rank, and assignment.
          You agree to maintain accurate and up-to-date information at all times.
        </p>
        <p>
          You are responsible for maintaining the confidentiality of your login credentials
          and for all activity under your account.
        </p>
      </section>

      <section className="legal-section">
        <h2>4. Identity Verification and Security</h2>
        <p>
          Registration requires identity and law-enforcement eligibility verification. Users
          submit a photograph of a department-issued credential and a selfie. AWS Textract
          processes credential information, and AWS Rekognition performs a one-time facial
          comparison between the selfie and the credential photograph. Agency affiliation may
          also be verified through automated and manual review.
        </p>
        <p>
          Before submitting the credential image or selfie, users must provide express consent
          to collection and processing for identity and eligibility verification.
          After a final approval or rejection, scheduled cleanup normally removes credential
          and selfie images within 72 hours, though processing delays or failures may extend
          that timing. An undecided manual review is automatically marked rejected after
          30 days from the original submission, and its images and temporary verification
          information are removed through scheduled cleanup. Limited verification records
          become eligible for scheduled removal beginning 60 days after the original
          submission; cleanup runs monthly and may take longer. Extracted credential text
          has a separate 90-day expiration safeguard if it remains in a verification record.
          BlueWire uses service providers for credential analysis and one-time facial
          comparison and does not sell verification information or share it for unrelated
          purposes.
        </p>
        <p>
          BlueWire reserves the right to re-verify users at any time, request additional
          documentation, or suspend access if verification standards are no longer met.
        </p>
      </section>

      <section className="legal-section">
        <h2>5. User Conduct</h2>
        <p>When using BlueWire, you agree that you will not:</p>
        <ul>
          <li>Provide false or misleading information.</li>
          <li>Impersonate another user or individual.</li>
          <li>Use the platform for any unlawful purpose.</li>
          <li>Attempt to bypass or manipulate the identity verification process.</li>
          <li>Share or distribute sensitive information outside authorized law enforcement use.</li>
          <li>Attempt to disrupt or compromise the application's security.</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>6. Content</h2>
        <p>
          Users may create and share notes, BOLOs, and messages. You retain responsibility
          for any content you submit. BlueWire does not sell or share user data for commercial
          purposes, and shared content is visible only to other authenticated users as intended.
        </p>
      </section>

      <section className="legal-section">
        <h2>7. Private Notes and Fieldbook</h2>
        <p>
          BlueWire's Private Notes let a user keep person records and interaction notes
          for that user's own professional reference. A Private Note may include text,
          photographs, and facial-search data associated with a person record. Private
          facial-search collections are isolated by user.
        </p>
        <p>
          A Private Note belongs to the user who created it. Other users, including the
          person described in the note, cannot access that user's original Private Note.
          BlueWire may access, preserve, review, disclose, or remove Private Notes when
          necessary to operate the service, provide support, investigate misuse, enforce
          these Terms, protect users or the public, comply with legal obligations, or
          respond to valid legal process.
        </p>
        <p>
          Users may choose to contribute eligible information from a Private Note or person
          record to Fieldbook. Organization Fieldbook and Global Fieldbook are separate
          sharing scopes: organization records are available only within the applicable
          organization to users authorized for that scope; global records are available to
          verified users with Global Fieldbook access. Fieldbook presents shared records
          or snapshots as read-only to other users. Authorized organization members may add
          separate interactions to an organization record. Sharing does not give another
          user access to the original Private Note. Some global snapshots may reflect later
          changes to the source note or person record, as described in the sharing controls.
        </p>
        <p>
          Fieldbook searches return a limited set of eligible records within the selected
          access scope. Results are informational leads and may be outdated, incomplete, or
          inaccurate. Users are responsible for information they create or share and must
          independently verify it before relying on it. Users may not use Private Notes or
          Fieldbook to store or share unlawful, threatening, harassing, defamatory,
          discriminatory, or otherwise prohibited content. Do not include sensitive
          personal, confidential law-enforcement, criminal-justice, medical, or other
          restricted information unless legally authorized to do so.
        </p>
      </section>

      <section className="legal-section">
        <h2>8. Public Booking Records and Facial Recognition</h2>
        <p>
          BlueWire may obtain booking photographs and limited accompanying information
          from publicly accessible county booking-record websites. Automated software
          navigates those public interfaces, accesses individual records, and retrieves
          photographs and selected identifying information through the same general
          navigation and retrieval actions available to a person using the public site.
          Current sources include Wake, Mecklenburg, and Harnett counties in North Carolina.
          The accompanying source information is limited to a person's name, the date
          associated with the photograph or source entry, the county, and a source-record
          reference. BlueWire does not retain criminal charges, convictions, case
          proceedings, sentencing information, or other criminal-history details from
          these source records.
        </p>
        <p>
          Where public booking-photo facial search is enabled, BlueWire uses AWS Rekognition
          to create facial-search indexes and compare a search photograph with the public
          booking-photo collection. A result is a potential facial match, not a verified
          or conclusive identification. A booking photograph or match is not proof of guilt,
          conviction, or current criminal status. Source information may be outdated or
          incomplete. Users must independently verify identity and current information
          through appropriate official sources before relying on a result.
        </p>
        <p>
          BlueWire is not a substitute for official criminal-justice systems, records,
          or investigative procedures. Users are responsible for lawful, authorized
          professional use of the feature and for complying with applicable law and
          agency policy. Public booking records are retained for up to ten years from
          BlueWire's first observation, subject to earlier removal or suppression where
          applicable. Automated cleanup mechanisms are implemented, but deletion timing
          depends on the relevant cleanup process being active and completing successfully.
        </p>
      </section>

      <section className="legal-section">
        <h2>9. News and Third-Party Content</h2>
        <p>
          The News page may display or aggregate articles, headlines, excerpts, images, links,
          and other content from third-party publishers and publicly available sources. BlueWire
          does not own, create, control, or endorse third-party news content, and all such content
          remains the property and responsibility of its original publisher or source.
        </p>
        <p>
          News content may be filtered, ranked, selected, or organized for law enforcement
          relevance; however, BlueWire does not guarantee the accuracy, completeness, timeliness,
          availability, or reliability of any third-party news content. Users should review the
          original publisher's article and source link for the complete and most current information.
        </p>
        <p>
          BlueWire may remove, hide, restrict, or disable access to news content at its discretion,
          including content that is outdated, unavailable, irrelevant, reported, or otherwise
          unsuitable for the platform.
        </p>
        <p>
          Any comments, replies, reactions, or discussions posted by users on news articles are
          user-generated content. Users are responsible for the content they post and must not post
          unlawful, abusive, threatening, harassing, defamatory, obscene, discriminatory, or otherwise
          objectionable material. BlueWire may review, moderate, remove, restrict, or report
          user-generated content at its discretion and may suspend or terminate accounts that violate
          these Terms or applicable law.
        </p>
        <p>
          Users may report objectionable content or abusive users through the app's reporting tools
          or by contacting BlueWire support.
        </p>
      </section>

      <section className="legal-section">
        <h2>10. Data Retention and Deletion</h2>
        <p>
          BlueWire retains user data only as long as necessary to operate the platform and maintain
          security.
        </p>
        <p>
          Verification images are subject to the image-retention process described in Section 4,
          including scheduled cleanup after a final decision or expiration of an undecided review.
        </p>
        <p>
          Limited verification metadata is subject to scheduled document cleanup beginning
          approximately 60 days after submission. Extracted credential text has an independent
          90-day expiration safeguard if it remains in a verification record.
        </p>
        <p>
          New Private Profile Notes are subject to a retention period. Ordinary private notes expire
          three years after their original note date. Private person records expire three years after
          the most recent interaction on that person record. Users may extend an expiring note or
          person record for additional three-month periods when the app presents an expiration
          countdown.
        </p>
        <p>
          When a Private Profile Note or person record expires or is deleted, BlueWire may delete the
          related private record, attachments, profile photos, facial-search enrollment data, search
          mappings, temporary files, shared read-only copies, and related cached or reference data.
          BlueWire may retain a minimal destruction log for auditing that does not include the note
          narrative, names, photographs, or facial identifiers.
        </p>
        <p>
          Deleting your account will remove your personal profile information and Private Profile
          Notes. Certain shared content, such as messages, may remain visible to other users if it has
          already been distributed within the platform.
        </p>
      </section>

      <section className="legal-section">
        <h2>11. No Guarantee of Identity</h2>
        <p>
          While BlueWire implements identity verification measures, we do not guarantee the identity,
          credentials, or conduct of any user. Users are responsible for exercising their own judgment
          when interacting with others on the platform.
        </p>
      </section>

      <section className="legal-section">
        <h2>12. Appropriate Use</h2>
        <p>
          BlueWire is intended to support professional communication and information sharing among
          authorized users. The platform is not a substitute for official law enforcement systems,
          databases, or dispatch channels.
        </p>
        <p>
          Users are responsible for complying with all applicable laws, agency policies, and
          regulations when using BlueWire.
        </p>
      </section>

      <section className="legal-section">
        <h2>13. Termination</h2>
        <p>
          BlueWire reserves the right to suspend or terminate accounts that violate these Terms of
          Service or compromise the security of the platform.
        </p>
      </section>

      <section className="legal-section">
        <h2>14. Disclaimer of Warranties</h2>
        <p>
          BlueWire is provided on an "as is" and "as available" basis. We make no warranties or
          representations regarding the reliability, availability, or accuracy of the application.
        </p>
      </section>

      <section className="legal-section">
        <h2>15. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, BlueWire shall not be liable for any indirect,
          incidental, or consequential damages resulting from your use of the application.
        </p>
      </section>

      <section className="legal-section">
        <h2>16. Changes to These Terms</h2>
        <p>
          We may update these Terms of Service from time to time. Any changes will be posted at this
          URL, and continued use of BlueWire constitutes acceptance of the updated terms.
        </p>
      </section>

      <section className="legal-section">
        <h2>17. Contact</h2>
        <p>
          For questions regarding these Terms of Service, please contact us at:{" "}
          <a href="mailto:thinlineteam@gmail.com">thinlineteam@gmail.com</a>.
        </p>
      </section>
    </main>
  );
}
